import { jwtDecode, JwtPayload } from "jwt-decode";
import {
    AnalyticsLocalStore,
    AnalyticsSessionStore,
    SessionStoreKey,
    StorageService,
    WindowWithStorage
} from "../../storage";
import { AnalyticsStorageItemList } from "./analytics-storage-item-list.interface";
import { TicketItem } from "../entity";
import { ObjectUtils } from "../../object";

export interface CheckoutData {
    token: string;
}

export interface ActiveOrderData extends JwtPayload {
    orderId: string;
    exp: number;
}

export class AnalyticsStorage {

    private static instance: AnalyticsStorage;

    private checkoutIdentifier = 'DWS-checkout';
    private analyticsIdentifier = 'DWS-analytics';

    constructor(private storageService: StorageService) {
        this.pushDataToSessionStorage({poolId: storageService.getConfig().poolId});
        this.handleLocalStoreSetup();
    }

    public static getInstance(): AnalyticsStorage | null {
        const storageInstance = this.getStorageInstance();

        if (!storageInstance) {
            return null;
        }

        if (!this.instance) {
            return new AnalyticsStorage(storageInstance);
        }

        return this.instance;
    }

    public getLocalStore(): AnalyticsLocalStore | null {
        const localStore = this.storageService.getItem(`${this.analyticsIdentifier}|${location.hostname}`);

        return localStore ? JSON.parse(localStore) as AnalyticsLocalStore : null;
    }

    public clearCartItems(): void {
        const localStore = this.getLocalStore();

        if (localStore) {
            localStore.cartItems = undefined;
            this.storageService.setItem(`${this.analyticsIdentifier}|${location.hostname}`, JSON.stringify(localStore));
        }
    }

    public getSessionStore(): AnalyticsSessionStore | undefined {
        const sessionStore = this.storageService.loadSession();

        return sessionStore[SessionStoreKey.AnalyticsData];
    }

    public pushDataToSessionStorage(data: Partial<AnalyticsSessionStore>) {
        const sessionStoreClone = ObjectUtils.deepClone(this.storageService.loadSession());
        sessionStoreClone[SessionStoreKey.AnalyticsData] = {
            ...sessionStoreClone[SessionStoreKey.AnalyticsData],
            ...data,
        };

        this.storageService.saveSession(sessionStoreClone);
    }

    public updateLocalStore(data: Partial<AnalyticsLocalStore>): void {
        const localStore = this.getLocalStore();

        if (!localStore) {
            this.storageService.setItem(`${this.analyticsIdentifier}|${location.hostname}`, JSON.stringify(data));

            return;
        }

        const updatedLocalStore = {
            ...localStore,
            ...data,
        }

        if (data.cartItems && (!localStore.cartItems || !localStore.cartItems.length)) {
            updatedLocalStore.firstAdditionTimestamp = Date.now();
        }

        this.storageService.setItem(`${this.analyticsIdentifier}|${location.hostname}`, JSON.stringify(updatedLocalStore));
    }

    public pushPurchasedOrderId(orderId: string): void {
        const localStore = this.getLocalStore();

        if (!localStore) {
            return;
        }

        localStore.completedOrders = localStore.completedOrders ? [...localStore.completedOrders, orderId] : [orderId];
        this.storageService.setItem(`${this.analyticsIdentifier}|${location.hostname}`, JSON.stringify(localStore));
    }

    public pushCartItems(tickets: TicketItem[]) {
        const localStore = this.getLocalStore();

        if (!localStore) {
            this.storageService.setItem(`${this.analyticsIdentifier}|${location.hostname}`, JSON.stringify({
                cartItems: [tickets],
            }));

            return;
        }

        localStore.cartItems = localStore.cartItems ? [...localStore.cartItems, ...tickets] : tickets;
        this.storageService.setItem(`${this.analyticsIdentifier}|${location.hostname}`, JSON.stringify(localStore));
    }

    public pushItemListToSessionStorage(data: AnalyticsStorageItemList) {
        const currentItemList = this.getSessionStore()!.itemLists;

        this.pushDataToSessionStorage({itemLists: currentItemList ? [...currentItemList, data] : [data]});
    }

    public getOrderData(): ActiveOrderData | null {
        const jwt = this.storageService.getItem(`${this.checkoutIdentifier}|${location.hostname}`);
        if (!jwt) {
            return null;
        }

        const checkoutData: CheckoutData = JSON.parse(atob(decodeURIComponent(jwt as string)));
        if (!checkoutData || !checkoutData.token) {
            return null;
        }

        const orderData: ActiveOrderData = jwtDecode(checkoutData.token);

        return orderData;
    }

    public removeOrderData() {
        this.storageService.removeItem(`${this.checkoutIdentifier}|${location.hostname}`);
    }

    public clearLocalStore(): void {
        const localStore = this.storageService.getItem(`${this.analyticsIdentifier}|${location.hostname}`) ?
            JSON.parse(this.storageService.getItem(`${this.analyticsIdentifier}|${location.hostname}`) as string)
            : null;

        if (this.containsPurchasedEvents(localStore)) {
            this.storageService.setItem(`${this.analyticsIdentifier}|${location.hostname}`, JSON.stringify({
                purchasedEvents: localStore.purchasedEvents,
            }));

            return;
        }

        this.storageService.removeItem(`${this.analyticsIdentifier}|${location.hostname}`);
    }

    private containsPurchasedEvents(localStore: AnalyticsLocalStore | null): boolean {
        if (!localStore) {
            return false;
        }

        return !!localStore.completedOrders && !!localStore.completedOrders.length;
    }

    private static getStorageInstance(): StorageService | undefined {
        const win: WindowWithStorage = window as WindowWithStorage & typeof globalThis;

        if (!win) {
            return undefined;
        }

        if (win.DWSStorageInstance === undefined) {
            return undefined;
        }

        return win.DWSStorageInstance;
    }

    private handleLocalStoreSetup(): void {
        const orderData = this.getOrderData();
        const localStore = this.getLocalStore();

        if (localStore && localStore.firstAdditionTimestamp) {
            const currentTime = Date.now();
            const timeDifferenceInMinutes = (currentTime - localStore.firstAdditionTimestamp) / (1000 * 60);

            if (timeDifferenceInMinutes > 20) {
                this.clearCartItems();
            }
        }

        if (orderData && orderData.orderId && localStore && localStore.orderId && atob(localStore.orderId) !== orderData.orderId) {
            this.clearLocalStore();

            return;
        }

        if (
            (!localStore || !localStore.orderId)
            && orderData
            && orderData.exp
            && orderData.exp - Math.floor(Date.now() / 1000) > 0
        ) {
            this.updateLocalStore({orderId: btoa(orderData.orderId)});
        }
    }

}
